import AuthService from "./AuthService";

/**
 * "Sign in with ANG Portal": the SPA's half of another application's sign-in,
 * and the administration of those applications.
 *
 * The provider sends the browser to #/oidc/<uid>. The page there finishes the
 * sign-in for the user signed in to this tab, or remembers it while the user
 * signs in first; every sign-in path then continues it (BaseController
 * navToAfterSignIn).
 */

export interface OidcInteraction {
	uid: string;
	/** The provider's step: "login" or "consent". */
	prompt: string;
	client: { clientId: string; name: string };
	/** The application wants a sign-in now, not the one this tab holds. */
	freshSignIn: boolean;
}

export interface PendingSignIn {
	uid: string;
	appName: string;
}

export interface OidcStatus {
	enabled: boolean;
	issuer: string | null;
	discoveryUrl: string | null;
	reason?: "no_public_url" | "no_encryption_key" | "failed";
}

export type OidcAuthMethod = "client_secret_basic" | "client_secret_post" | "none";

export interface OidcApp {
	id: number;
	clientId: string;
	name: string;
	authMethod: OidcAuthMethod;
	redirectUris: string[];
	postLogoutRedirectUris: string[];
	resourceIds: number[];
	allowRefresh: boolean;
	isActive: boolean;
	lastUsedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface OidcApi {
	id: number;
	name: string;
	identifier: string;
	scope: string;
	roleKey: string;
	allowedGroupIds: number[];
	accessTokenTtl: number;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export class OidcError extends Error {
	readonly status: number;
	readonly code?: string;

	constructor(message: string, status: number, code?: string) {
		super(message);
		this.status = status;
		this.code = code;
	}
}

const PENDING_KEY = "portal_oidc_pending";
const CONTINUED_KEY = "portal_oidc_continued";

async function readJson<T>(response: Response): Promise<T> {
	const data = (await response.json().catch(() => ({}))) as T & {
		message?: string;
		code?: string;
	};
	if (!response.ok) {
		throw new OidcError(
			data.message || `Request failed (${response.status})`,
			response.status,
			data.code,
		);
	}
	return data;
}

async function adminRequest<T>(
	path: string,
	method = "GET",
	body?: unknown,
): Promise<T> {
	const response = await AuthService.authenticatedFetch(path, {
		method,
		headers: body === undefined ? undefined : { "Content-Type": "application/json" },
		body: body === undefined ? undefined : JSON.stringify(body),
	});
	if (response.status === 204) return undefined as T;
	return readJson<T>(response);
}

const interactionPath = (uid: string) =>
	`/oidc/interaction/${encodeURIComponent(uid)}`;

const OidcService = {
	// ---- The sign-in hand-off -------------------------------------------

	async getInteraction(uid: string): Promise<OidcInteraction> {
		return readJson<OidcInteraction>(
			await fetch(`${interactionPath(uid)}/details`),
		);
	},

	/** Finish the sign-in as the user signed in to this tab; resolves to where the browser goes next. */
	async confirm(uid: string): Promise<string> {
		const response = await AuthService.authenticatedFetch(
			`${interactionPath(uid)}/confirm`,
			{ method: "POST" },
		);
		return (await readJson<{ redirectTo: string }>(response)).redirectTo;
	},

	async abort(uid: string): Promise<string> {
		const response = await fetch(`${interactionPath(uid)}/abort`, {
			method: "POST",
		});
		return (await readJson<{ redirectTo: string }>(response)).redirectTo;
	},

	rememberPending(pending: PendingSignIn): void {
		sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
	},

	getPending(): PendingSignIn | null {
		try {
			const raw = sessionStorage.getItem(PENDING_KEY);
			const parsed = raw ? (JSON.parse(raw) as PendingSignIn) : null;
			return parsed && typeof parsed.uid === "string" ? parsed : null;
		} catch {
			return null;
		}
	},

	/**
	 * The pending sign-in, removed: it is continued once. Marked as continued
	 * after a sign-in, so the page does not ask for that sign-in again; kept in
	 * sessionStorage because Microsoft sign-in reloads the page.
	 */
	takePending(): PendingSignIn | null {
		const pending = this.getPending();
		sessionStorage.removeItem(PENDING_KEY);
		if (pending) sessionStorage.setItem(CONTINUED_KEY, pending.uid);
		return pending;
	},

	/** Whether the user has just signed in to continue this sign-in. Asked once. */
	consumeContinued(uid: string): boolean {
		const continued = sessionStorage.getItem(CONTINUED_KEY);
		sessionStorage.removeItem(CONTINUED_KEY);
		return continued === uid;
	},

	// ---- Administration ---------------------------------------------------

	getStatus(): Promise<OidcStatus> {
		return adminRequest<OidcStatus>("/api/oidc/status");
	},

	async listApps(): Promise<OidcApp[]> {
		return (await adminRequest<{ items: OidcApp[] }>("/api/oidc/clients")).items;
	},

	createApp(
		app: Partial<OidcApp>,
	): Promise<{ item: OidcApp; clientSecret: string | null }> {
		return adminRequest("/api/oidc/clients", "POST", app);
	},

	updateApp(
		id: number,
		app: Partial<OidcApp>,
	): Promise<{ item: OidcApp; clientSecret: string | null }> {
		return adminRequest(`/api/oidc/clients/${id}`, "PATCH", app);
	},

	deleteApp(id: number): Promise<void> {
		return adminRequest(`/api/oidc/clients/${id}`, "DELETE");
	},

	rotateAppSecret(id: number): Promise<{ clientSecret: string }> {
		return adminRequest(`/api/oidc/clients/${id}/secret`, "POST");
	},

	revokeAppSignIns(id: number): Promise<{ revoked: number }> {
		return adminRequest(`/api/oidc/clients/${id}/revoke`, "POST");
	},

	async listApis(): Promise<OidcApi[]> {
		return (await adminRequest<{ items: OidcApi[] }>("/api/oidc/resources")).items;
	},

	createApi(api: Partial<OidcApi>): Promise<{ item: OidcApi }> {
		return adminRequest("/api/oidc/resources", "POST", api);
	},

	updateApi(id: number, api: Partial<OidcApi>): Promise<{ item: OidcApi }> {
		return adminRequest(`/api/oidc/resources/${id}`, "PATCH", api);
	},

	deleteApi(id: number): Promise<void> {
		return adminRequest(`/api/oidc/resources/${id}`, "DELETE");
	},

	rotateSigningKey(): Promise<{ kid: string }> {
		return adminRequest("/api/oidc/keys/rotate", "POST");
	},
};

export default OidcService;
